// ComplaintsPage.jsx - Gestion des plaintes
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, Plus, Search, Eye, CheckCircle, XCircle, ArrowRight, 
  Calendar, MapPin, User, AlertTriangle, Filter, Upload, Loader2
} from 'lucide-react';
import api from '../lib/api';
import PersonSearchCombo from '../components/shared/PersonSearchCombo';
import FileUploader from '../components/shared/FileUploader';

const COMPLAINT_TYPES = [
  { code: 'PLAINTE_SIMPLE', name: 'Plainte simple' },
  { code: 'PLAINTE_AVEC_CONSTITUTION', name: 'Plainte avec constitution de partie civile' },
  { code: 'DENONCIATION', name: 'Dénonciation' },
  { code: 'FLAGRANCE', name: 'Flagrance (constat direct)' },
  { code: 'MANDAT_PROCUREUR', name: 'Mandat du Procureur' }
];

const STATUS_BADGES = {
  RECEIVED: { label: 'Reçue', className: 'bg-blue-100 text-blue-800' },
  UNDER_REVIEW: { label: 'En examen', className: 'bg-yellow-100 text-yellow-800' },
  ACCEPTED: { label: 'Acceptée', className: 'bg-green-100 text-green-800' },
  REJECTED: { label: 'Rejetée', className: 'bg-red-100 text-red-800' },
  CLOSED: { label: 'Clôturée', className: 'bg-gray-100 text-gray-800' },
  TRANSFERRED: { label: 'Transférée', className: 'bg-purple-100 text-purple-800' }
};

const DRC_PROVINCES = [
  "KINSHASA", "KONGO-CENTRAL", "KWANGO", "KWILU", "MAI-NDOMBE",
  "KASAI", "KASAI-CENTRAL", "KASAI-ORIENTAL", "LOMAMI", "SANKURU",
  "MANIEMA", "SUD-KIVU", "NORD-KIVU", "ITURI", "HAUT-UELE",
  "TSHOPO", "BAS-UELE", "NORD-UBANGI", "SUD-UBANGI", "MONGALA",
  "EQUATEUR", "TSHUAPA", "TANGANYIKA", "HAUT-LOMAMI", "LUALABA", "HAUT-KATANGA"
];

const ComplaintsPage = () => {
  const { language, user } = useAuth();
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [offenses, setOffenses] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    complaint_type: 'PLAINTE_SIMPLE',
    plaintiff_id: '',
    plaintiff_name: '',
    plaintiff_national_id: '',
    plaintiff_phone: '',
    plaintiff_email: '',
    plaintiff_address: '',
    defendant_id: '',
    defendant_name: '',
    defendant_description: '',
    incident_date: '',
    incident_location: '',
    incident_description: '',
    offense_types: [],
    evidence_description: '',
    province: user?.province || ''
  });

  const loadComplaints = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('complaint_type', filterType);
      
      const response = await api.get(`/complaints?${params}`);
      setComplaints(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des plaintes');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus, filterType]);

  const loadOffenses = useCallback(async () => {
    try {
      const response = await api.get('/offenses');
      setOffenses(response.data || []);
    } catch (error) {
      console.log('No offenses loaded');
    }
  }, []);

  useEffect(() => {
    loadComplaints();
    loadOffenses();
  }, [loadComplaints, loadOffenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      // Create complaint with attachments
      const complaintData = {
        ...formData,
        attachments: attachments.map(f => f.file_id)
      };
      await api.post('/complaints', complaintData);
      toast.success('Plainte enregistrée avec succès');
      setShowNewDialog(false);
      resetForm();
      loadComplaints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle plaintiff selection from PersonSearchCombo
  const handlePlaintiffSelect = (person) => {
    if (person) {
      setFormData(prev => ({
        ...prev,
        plaintiff_id: person.user_id || person.id,
        plaintiff_name: person.name || `${person.first_name} ${person.last_name}`,
        plaintiff_national_id: person.national_id || '',
        plaintiff_phone: person.phone || '',
        plaintiff_email: person.email || '',
        plaintiff_address: person.address || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        plaintiff_id: '',
        plaintiff_name: '',
        plaintiff_national_id: '',
        plaintiff_phone: '',
        plaintiff_email: '',
        plaintiff_address: ''
      }));
    }
  };

  // Handle defendant selection
  const handleDefendantSelect = (person) => {
    if (person) {
      setFormData(prev => ({
        ...prev,
        defendant_id: person.user_id || person.id,
        defendant_name: person.name || `${person.first_name} ${person.last_name}`,
        defendant_description: prev.defendant_description || ''
      }));
    }
  };

  const handleCreateCase = async (complaintId) => {
    try {
      const response = await api.post(`/complaints/${complaintId}/create-case`);
      toast.success(`Dossier ${response.data.case_number} créé`);
      loadComplaints();
      setShowViewDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateStatus = async (complaintId, newStatus, reason = '') => {
    try {
      await api.put(`/complaints/${complaintId}`, { status: newStatus, rejection_reason: reason });
      toast.success('Statut mis à jour');
      loadComplaints();
      setShowViewDialog(false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      complaint_type: 'PLAINTE_SIMPLE',
      plaintiff_id: '',
      plaintiff_name: '',
      plaintiff_national_id: '',
      plaintiff_phone: '',
      plaintiff_email: '',
      plaintiff_address: '',
      defendant_id: '',
      defendant_name: '',
      defendant_description: '',
      incident_date: '',
      incident_location: '',
      incident_description: '',
      offense_types: [],
      evidence_description: '',
      province: user?.province || ''
    });
    setAttachments([]);
  };

  const viewComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setShowViewDialog(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="complaints-page">
        {/* Header - Mobile Optimized */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
              {language === 'fr' ? 'Registre des Plaintes' : 'Complaints Registry'}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">
              {language === 'fr' ? 'Gérer les plaintes et dénonciations' : 'Manage complaints and reports'}
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setShowNewDialog(true)} size="sm" data-testid="new-complaint-btn">
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{language === 'fr' ? 'Nouvelle Plainte' : 'New Complaint'}</span>
              <span className="sm:hidden">{language === 'fr' ? 'Nouveau' : 'New'}</span>
            </Button>
          </div>
        </div>

        {/* Stats - Mobile Optimized */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <Card>
            <CardContent className="p-3 sm:pt-6 text-center">
              <p className="text-xl sm:text-3xl font-bold">{complaints.length}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">{language === 'fr' ? 'Total' : 'Total'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:pt-6 text-center">
              <p className="text-xl sm:text-3xl font-bold text-blue-600">
                {complaints.filter(c => c.status === 'RECEIVED').length}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground">{language === 'fr' ? 'Reçues' : 'Received'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:pt-6 text-center">
              <p className="text-xl sm:text-3xl font-bold text-yellow-600">
                {complaints.filter(c => c.status === 'UNDER_REVIEW').length}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground">{language === 'fr' ? 'En examen' : 'Under Review'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:pt-6 text-center">
              <p className="text-xl sm:text-3xl font-bold text-green-600">
                {complaints.filter(c => c.status === 'ACCEPTED').length}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground">{language === 'fr' ? 'Acceptées' : 'Accepted'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters - Mobile Optimized */}
        <Card>
          <CardContent className="p-3 sm:pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
              <div className="relative sm:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les statuts' : 'All statuses'}</SelectItem>
                  {Object.entries(STATUS_BADGES).map(([code, info]) => (
                    <SelectItem key={code} value={code}>{info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les types' : 'All types'}</SelectItem>
                  {COMPLAINT_TYPES.map(t => (
                    <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Complaints List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-4">
            {complaints.map((complaint) => (
              <Card key={complaint.complaint_id} className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => viewComplaint(complaint)}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                          {complaint.complaint_number}
                        </span>
                        <Badge className={STATUS_BADGES[complaint.status]?.className}>
                          {STATUS_BADGES[complaint.status]?.label}
                        </Badge>
                        <Badge variant="outline">
                          {COMPLAINT_TYPES.find(t => t.code === complaint.complaint_type)?.name}
                        </Badge>
                      </div>
                      <h3 className="font-semibold">
                        {complaint.plaintiff_name} c/ {complaint.defendant_name || 'X'}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {complaint.incident_description}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(complaint.incident_date).toLocaleDateString('fr-FR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {complaint.incident_location}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {complaint.province}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {complaint.case_id && (
                        <Badge variant="default" className="gap-1">
                          <ArrowRight className="w-3 h-3" /> Dossier créé
                        </Badge>
                      )}
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); viewComplaint(complaint); }}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {complaints.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                {language === 'fr' ? 'Aucune plainte trouvée' : 'No complaints found'}
              </div>
            )}
          </div>
        )}

        {/* New Complaint Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{language === 'fr' ? 'Nouvelle Plainte' : 'New Complaint'}</DialogTitle>
              <DialogDescription>
                {language === 'fr' ? 'Enregistrez une nouvelle plainte ou dénonciation' : 'Register a new complaint'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                <div className="md:col-span-2">
                  <Label>{language === 'fr' ? 'Type de plainte' : 'Complaint Type'} *</Label>
                  <Select value={formData.complaint_type} onValueChange={(v) => setFormData({...formData, complaint_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COMPLAINT_TYPES.map(t => (
                        <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {language === 'fr' ? 'Plaignant' : 'Plaintiff'}
                  </h4>
                </div>
                <div className="md:col-span-2">
                  <PersonSearchCombo
                    label={language === 'fr' ? 'Rechercher le plaignant' : 'Search plaintiff'}
                    placeholder={language === 'fr' ? 'Tapez le nom ou UNJI...' : 'Type name or UNJI...'}
                    onPersonSelect={handlePlaintiffSelect}
                    showCreateOption={true}
                  />
                </div>
                {formData.plaintiff_name && (
                  <>
                    <div>
                      <Label>{language === 'fr' ? 'N° Carte d\'identité' : 'ID Number'}</Label>
                      <Input value={formData.plaintiff_national_id} onChange={(e) => setFormData({...formData, plaintiff_national_id: e.target.value})} />
                    </div>
                    <div>
                      <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                      <Input value={formData.plaintiff_phone} onChange={(e) => setFormData({...formData, plaintiff_phone: e.target.value})} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={formData.plaintiff_email} onChange={(e) => setFormData({...formData, plaintiff_email: e.target.value})} />
                    </div>
                    <div>
                      <Label>{language === 'fr' ? 'Adresse' : 'Address'}</Label>
                      <Input value={formData.plaintiff_address} onChange={(e) => setFormData({...formData, plaintiff_address: e.target.value})} />
                    </div>
                  </>
                )}

                <div className="md:col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-medium mb-3">{language === 'fr' ? 'Mis en cause (si connu)' : 'Defendant'}</h4>
                </div>
                <div className="md:col-span-2">
                  <PersonSearchCombo
                    label={language === 'fr' ? 'Rechercher le mis en cause' : 'Search defendant'}
                    placeholder={language === 'fr' ? 'Tapez le nom ou laissez vide si inconnu...' : 'Type name or leave empty if unknown...'}
                    onPersonSelect={handleDefendantSelect}
                    showCreateOption={true}
                    required={false}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>{language === 'fr' ? 'Description (si inconnu ou complément)' : 'Description'}</Label>
                  <Input value={formData.defendant_description} onChange={(e) => setFormData({...formData, defendant_description: e.target.value})} placeholder={language === 'fr' ? 'Signalement physique, vêtements...' : 'Physical description, clothing...'} />
                </div>

                <div className="md:col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-medium mb-3">{language === 'fr' ? 'Faits' : 'Facts'}</h4>
                </div>
                <div>
                  <Label>{language === 'fr' ? 'Date de l\'incident' : 'Incident Date'} *</Label>
                  <Input type="date" value={formData.incident_date} onChange={(e) => setFormData({...formData, incident_date: e.target.value})} required />
                </div>
                <div>
                  <Label>{language === 'fr' ? 'Lieu' : 'Location'} *</Label>
                  <Input value={formData.incident_location} onChange={(e) => setFormData({...formData, incident_location: e.target.value})} required />
                </div>
                <div>
                  <Label>Province *</Label>
                  <Select value={formData.province} onValueChange={(v) => setFormData({...formData, province: v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      {DRC_PROVINCES.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{language === 'fr' ? 'Infractions alléguées' : 'Alleged Offenses'}</Label>
                  <Select onValueChange={(v) => {
                    if (!formData.offense_types.includes(v)) {
                      setFormData({...formData, offense_types: [...formData.offense_types, v]});
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Ajouter..." /></SelectTrigger>
                    <SelectContent>
                      {offenses.map(o => (
                        <SelectItem key={o.offense_id} value={o.code}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.offense_types.map((ot, i) => (
                      <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setFormData({...formData, offense_types: formData.offense_types.filter((_, idx) => idx !== i)})}>
                        {ot} ×
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label>{language === 'fr' ? 'Description des faits' : 'Facts Description'} *</Label>
                  <Textarea value={formData.incident_description} onChange={(e) => setFormData({...formData, incident_description: e.target.value})} rows={4} required />
                </div>
                <div className="md:col-span-2">
                  <Label>{language === 'fr' ? 'Preuves / éléments (description)' : 'Evidence (description)'}</Label>
                  <Textarea value={formData.evidence_description} onChange={(e) => setFormData({...formData, evidence_description: e.target.value})} rows={2} />
                </div>

                {/* File Attachments Section */}
                <div className="md:col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {language === 'fr' ? 'Pièces jointes' : 'Attachments'}
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {language === 'fr' 
                      ? 'Ajoutez des photos, documents, audio ou vidéo comme preuves'
                      : 'Add photos, documents, audio or video as evidence'}
                  </p>
                  <FileUploader
                    value={attachments}
                    onChange={setAttachments}
                    entityType="complaint"
                    maxFiles={20}
                    maxFileSize={100 * 1024 * 1024}
                    acceptedTypes="image/*,video/*,audio/*,.pdf,.doc,.docx"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)}>
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {language === 'fr' ? 'Enregistrer' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Complaint Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedComplaint && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className="font-mono">{selectedComplaint.complaint_number}</span>
                    <Badge className={STATUS_BADGES[selectedComplaint.status]?.className}>
                      {STATUS_BADGES[selectedComplaint.status]?.label}
                    </Badge>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Type' : 'Type'}</p>
                      <p className="font-medium">{COMPLAINT_TYPES.find(t => t.code === selectedComplaint.complaint_type)?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Province</p>
                      <p className="font-medium">{selectedComplaint.province}</p>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">{language === 'fr' ? 'Plaignant' : 'Plaintiff'}</h4>
                    <p><strong>{selectedComplaint.plaintiff_name}</strong></p>
                    {selectedComplaint.plaintiff_phone && <p className="text-sm">Tél: {selectedComplaint.plaintiff_phone}</p>}
                    {selectedComplaint.plaintiff_address && <p className="text-sm">{selectedComplaint.plaintiff_address}</p>}
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">{language === 'fr' ? 'Mis en cause' : 'Defendant'}</h4>
                    <p><strong>{selectedComplaint.defendant_name || 'Inconnu'}</strong></p>
                    {selectedComplaint.defendant_description && <p className="text-sm">{selectedComplaint.defendant_description}</p>}
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">{language === 'fr' ? 'Faits' : 'Facts'}</h4>
                    <p className="text-sm"><strong>{language === 'fr' ? 'Date:' : 'Date:'}</strong> {new Date(selectedComplaint.incident_date).toLocaleDateString('fr-FR')}</p>
                    <p className="text-sm"><strong>{language === 'fr' ? 'Lieu:' : 'Location:'}</strong> {selectedComplaint.incident_location}</p>
                    <p className="mt-2">{selectedComplaint.incident_description}</p>
                    {selectedComplaint.offense_types?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selectedComplaint.offense_types.map((ot, i) => (
                          <Badge key={i} variant="destructive">{ot}</Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedComplaint.case_id && (
                    <div className="border-t pt-4">
                      <Badge variant="default" className="gap-1">
                        <CheckCircle className="w-3 h-3" /> Dossier créé: {selectedComplaint.case_id}
                      </Badge>
                      <Button variant="link" onClick={() => navigate(`/cases/${selectedComplaint.case_id}`)}>
                        Voir le dossier →
                      </Button>
                    </div>
                  )}
                </div>
                <DialogFooter className="flex-wrap gap-2">
                  {selectedComplaint.status === 'RECEIVED' && (
                    <>
                      <Button variant="outline" onClick={() => handleUpdateStatus(selectedComplaint.complaint_id, 'UNDER_REVIEW')}>
                        <Eye className="w-4 h-4 mr-2" /> Mettre en examen
                      </Button>
                      <Button variant="destructive" onClick={() => handleUpdateStatus(selectedComplaint.complaint_id, 'REJECTED', 'Motif à préciser')}>
                        <XCircle className="w-4 h-4 mr-2" /> Rejeter
                      </Button>
                    </>
                  )}
                  {selectedComplaint.status === 'UNDER_REVIEW' && !selectedComplaint.case_id && (
                    <Button onClick={() => handleCreateCase(selectedComplaint.complaint_id)}>
                      <ArrowRight className="w-4 h-4 mr-2" /> Créer un dossier
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                    {language === 'fr' ? 'Fermer' : 'Close'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ComplaintsPage;
